import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment'
import { Button, Calendar, EmptyState } from '@enamelai/design-system'
import { Icon as CalendarIcon } from '@enamelai/design-system/icons/Calendar'
import { Icon as ChevronDownIcon } from '@enamelai/design-system/icons/Arrow_down_chevron'
import { Icon as ChevronLeftIcon } from '@enamelai/design-system/icons/Pagination_chevron_left'
import { Icon as ChevronRightIcon } from '@enamelai/design-system/icons/Pagination_chevron_right'
import {
  getSettingsRouteById,
  getSettingsRouteByPathname,
} from '@pages/Settings/settingsRoutes'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { useAppSelector } from '@store/hooks'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { buildClinicPdfHeaderPayload } from '@utils/clinic.util'
import { downloadWorkScheduleAsPdf } from '@utils/workSchedulePdfExport.util'
import { usePagedRows } from '../../shared/settingsTable'
import { useStaffShifts } from './hooks/useStaffShifts'
import { useClinicClosedWeekdays } from './hooks/useClinicClosedWeekdays'
import WorkScheduleTable, {
  type WorkScheduleDayColumn,
} from './WorkScheduleTable.component'
import './WorkScheduleStyle.css'
import './WorkSchedulePdfExport.css'

/** The Figma shows only the current week; flip to true to bring back week navigation. */
const SHOW_WEEK_CONTROLS = false

const buildDays = (rangeStart: moment.Moment): moment.Moment[] =>
  Array.from({ length: 7 }, (_, i) => rangeStart.clone().add(i, 'day'))

const WorkSchedule = (): React.ReactElement => {
  const location = useLocation()
  const navigate = useNavigate()
  const { setHeaderTitle, setHeaderButtons } = useSettingsHeader()
  const selectedClinic = useAppSelector((s) => s.clinic.selectedClinic)
  const clinicId = selectedClinic?.clinic_id ?? ''
  const { showToast } = useNotificationToast()
  const [rangeStart, setRangeStart] = useState(() => moment().startOf('day'))
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const calendarAnchorRef = useRef<HTMLDivElement>(null)

  const pageTitle = useMemo(
    () =>
      getSettingsRouteByPathname(location.pathname)?.name ?? 'Work schedule',
    [location.pathname],
  )

  const days = useMemo(() => buildDays(rangeStart), [rangeStart])
  const rangeEnd = days[days.length - 1]

  const dayColumns = useMemo(
    (): WorkScheduleDayColumn[] =>
      days.map((d) => ({
        name: d.format('dddd'),
        date: d.format('MM/DD/YYYY'),
        isToday: d.isSame(moment(), 'day'),
      })),
    [days],
  )

  const closedWeekdays = useClinicClosedWeekdays(clinicId)
  const { staffWeeks, loading } = useStaffShifts(days, closedWeekdays)
  const { pageRows, pagination } = usePagedRows(staffWeeks)

  const rangeLabel = `${rangeStart.format('MMMM D, YYYY')} - ${rangeEnd.format(
    'MMMM D, YYYY',
  )}`

  useEffect(() => {
    if (!isCalendarOpen) return
    const handlePointerDown = (event: MouseEvent): void => {
      if (!calendarAnchorRef.current?.contains(event.target as Node)) {
        setIsCalendarOpen(false)
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [isCalendarOpen])

  const shiftRange = useCallback((dayCount: number): void => {
    setRangeStart((m) => m.clone().add(dayCount, 'days'))
    setIsCalendarOpen(false)
  }, [])

  const handleDateSelect = useCallback((date: Date): void => {
    setRangeStart(moment(date).startOf('day'))
    setIsCalendarOpen(false)
  }, [])

  const handleAddStaff = useCallback((): void => {
    navigate(
      getSettingsRouteById('staff-list')?.fullPath ??
        '/settings/team/staff-list',
    )
  }, [navigate])

  const handleExportPdf = useCallback((): void => {
    if (exporting) return
    setExporting(true)
    void (async (): Promise<void> => {
      try {
        const header = buildClinicPdfHeaderPayload(selectedClinic)
        await downloadWorkScheduleAsPdf({
          clinicName: header.clinicName,
          clinicAddress: header.clinicAddress ?? '',
          rangeLabel,
          dayHeaders: dayColumns,
          staffWeeks,
        })
        showToast?.(
          createSuccessToast(
            'PDF downloaded',
            'Work schedule exported successfully.',
          ),
        )
      } catch {
        showToast?.(
          createFailedToast(
            'Export failed',
            'Could not export work schedule. Please try again.',
          ),
        )
      } finally {
        setExporting(false)
      }
    })()
  }, [exporting, selectedClinic, rangeLabel, dayColumns, staffWeeks, showToast])

  useEffect(() => {
    setHeaderTitle(
      <div className="ws-header-title-row">
        <span>{pageTitle}</span>
        {SHOW_WEEK_CONTROLS ? (
          <div className="ws-range-controls">
            <div className="ws-calendar-anchor" ref={calendarAnchorRef}>
              <Button
                className="settings-ds-button"
                variant="secondary"
                size="md"
                type="button"
                aria-expanded={isCalendarOpen}
                leftIcon={<CalendarIcon aria-hidden="true" />}
                rightIcon={<ChevronDownIcon aria-hidden="true" />}
                onClick={() => setIsCalendarOpen((open) => !open)}
              >
                {rangeLabel}
              </Button>
              {isCalendarOpen ? (
                <Calendar
                  className="ws-calendar-popover"
                  value={rangeStart.toDate()}
                  onChange={handleDateSelect}
                />
              ) : null}
            </div>
            <Button
              className="settings-ds-button"
              variant="tertiary"
              size="md"
              type="button"
              aria-label="Previous week"
              leftIcon={<ChevronLeftIcon aria-hidden="true" />}
              onClick={() => shiftRange(-7)}
            />
            <Button
              className="settings-ds-button"
              variant="tertiary"
              size="md"
              type="button"
              aria-label="Next week"
              leftIcon={<ChevronRightIcon aria-hidden="true" />}
              onClick={() => shiftRange(7)}
            />
          </div>
        ) : null}
      </div>,
    )
  }, [
    pageTitle,
    rangeLabel,
    rangeStart,
    isCalendarOpen,
    handleDateSelect,
    shiftRange,
    setHeaderTitle,
  ])

  useEffect(() => () => setHeaderTitle(null), [setHeaderTitle])

  useEffect(() => {
    setHeaderButtons(
      <Button
        className="settings-ds-button"
        variant="primary"
        size="md"
        type="button"
        loading={exporting}
        disabled={exporting || loading || staffWeeks.length === 0}
        onClick={handleExportPdf}
      >
        Export schedule
      </Button>,
    )
    return () => setHeaderButtons(null)
  }, [exporting, loading, staffWeeks.length, handleExportPdf, setHeaderButtons])

  if (loading) {
    return <div className="ws-page" />
  }

  return (
    <div className="ws-page" id="scp-work-schedule">
      {staffWeeks.length === 0 ? (
        <EmptyState
          className="ws-empty"
          iconClass="mgc mgc_calendar_week_fill"
          title="No staff found"
          description="You don't have any staff on record yet."
          actionLabel="Add staff"
          onAction={handleAddStaff}
        />
      ) : (
        <WorkScheduleTable
          days={dayColumns}
          staffWeeks={pageRows}
          pagination={pagination}
        />
      )}
    </div>
  )
}

export default WorkSchedule
